import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Users, Plus, Settings, BarChart3, 
  ChevronRight, Globe, Calendar, CheckCircle, XCircle, 
  Clock, TrendingUp, LogOut, RefreshCw, Mail,
  UserPlus, Trash2, Eye, Send, AlertCircle,
  Search, Filter, X, Copy, ExternalLink, Archive,
  PlayCircle, History, Shield, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import NotificationBell from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cross-Org Analytics Component
const CrossOrgAnalytics = ({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_data = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/cross-org-analytics`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch_data();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="text-center py-12"><div className="text-gray-400">Loading analytics...</div></div>;
  if (!data) return <div className="text-center py-12 text-gray-400">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Organizations', val: data.totals.organizations, color: '#E86C3D' },
          { label: 'Programs', val: data.totals.programs, color: '#8B5CF6' },
          { label: 'Total Users', val: data.totals.users, color: '#3B82F6' },
          { label: 'Youth', val: data.totals.youth, color: '#10B981' },
          { label: 'Staff', val: data.totals.staff, color: '#F59E0B' },
          { label: 'Admins', val: data.totals.admins, color: '#EC4899' },
        ].map(kpi => (
          <Card key={kpi.label} className="p-4 rounded-2xl text-center bg-white">
            <p className="text-3xl font-black" style={{ color: kpi.color }}>{kpi.val}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Button onClick={() => {
          const clientOrg = data.organizations.find(o => o.id !== '565d97be-8f20-4821-b53a-d1779581b61c');
          if (clientOrg) navigate(`/platform/org/${clientOrg.id}`);
        }} className="h-auto py-3 rounded-xl bg-[#1B2D4F] hover:bg-[#152440] text-white flex-col gap-1">
          <Building2 className="w-5 h-5" />
          <span className="text-xs">{data.organizations.find(o => o.id !== '565d97be-8f20-4821-b53a-d1779581b61c')?.name?.split(' ').slice(0,3).join(' ') || 'View Client Org'}</span>
        </Button>
        <Button onClick={() => navigate('/admin/completion-tracker')} className="h-auto py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex-col gap-1">
          <CheckCircle className="w-5 h-5" />
          <span className="text-xs">Completion Tracker</span>
        </Button>
        <Button onClick={() => navigate('/admin/analytics')} className="h-auto py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white flex-col gap-1">
          <BarChart3 className="w-5 h-5" />
          <span className="text-xs">Visual Analytics</span>
        </Button>
        <Button onClick={() => navigate('/guide')} className="h-auto py-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex-col gap-1">
          <HelpCircle className="w-5 h-5" />
          <span className="text-xs">Youth Guide</span>
        </Button>
      </div>

      {/* Activity Metrics */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-gray-900 mb-4">Platform Activity</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: 'Goals Set', val: data.activity.goals },
            { label: 'Wins Posted', val: data.activity.wins },
            { label: 'Check-ins', val: data.activity.checkins },
            { label: 'Assessments', val: data.activity.assessments },
            { label: 'Courses Done', val: data.activity.courses },
            { label: 'Projects', val: data.activity.projects },
            { label: 'Messages', val: data.activity.messages },
          ].map(m => (
            <div key={m.label} className="p-3 rounded-xl bg-gray-50 text-center">
              <p className="text-xl font-bold text-gray-900">{m.val}</p>
              <p className="text-[9px] text-gray-500">{m.label}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Organizations List */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-gray-900 mb-4">Client Organizations</h3>
        <div className="space-y-3">
          {data.organizations.map(org => (
            <div key={org.id} 
              className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer"
              onClick={() => navigate(`/platform/org/${org.id}`)}
            >
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="w-12 h-12 rounded-xl object-contain bg-white p-1" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">{(org.name || org.slug || '?').charAt(0).toUpperCase()}</div>
              )}
              <div className="flex-1">
                <p className="font-bold text-sm text-gray-900">{org.name || org.slug}</p>
                <p className="text-[10px] text-gray-500">{org.programs} programs · {org.youth} youth</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={org.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>{org.status}</Badge>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
          {data.organizations.length === 0 && <p className="text-center text-gray-400 py-4">No organizations yet</p>}
        </div>
      </Card>

      {/* Recent Demo Requests */}
      <Card className="p-5 rounded-2xl bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-sm text-gray-900">Demo Requests ({data.demos.total})</h3>
        </div>
        <div className="space-y-2">
          {data.demos.recent.map((d, i) => (
            <div key={i} className="p-3 rounded-xl bg-orange-50 flex items-center gap-3">
              <Mail className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{d.name} — {d.organization}</p>
                <p className="text-[9px] text-gray-500">{d.email} · {d.program_type} · {d.youth_count} youth</p>
              </div>
            </div>
          ))}
          {data.demos.recent.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No demo requests yet</p>}
        </div>
      </Card>
    </div>
  );
};

// Billing Section Component
const BillingSection = () => {
  const [billingData, setBillingData] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddSub, setShowAddSub] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [newSub, setNewSub] = useState({ org_id: '', plan: 'professional', billing_cycle: 'monthly', seats: 100 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchBilling = async () => {
      const token = localStorage.getItem('token');
      try {
        const [subRes, planRes, orgRes] = await Promise.all([
          fetch(`${API_URL}/api/billing/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/billing/plans`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/platform/organizations`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (subRes.ok) setBillingData(await subRes.json());
        if (planRes.ok) setPlans(await planRes.json());
        if (orgRes.ok) {
          const orgData = await orgRes.json();
          setOrgs(orgData.organizations || orgData || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchBilling();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSubscription = async () => {
    if (!newSub.org_id) { toast.error('Select an organization'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/subscriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newSub)
      });
      if (res.ok) {
        toast.success('Subscription created!');
        setShowAddSub(false);
        // Refresh
        const subRes = await fetch(`${API_URL}/api/billing/subscriptions`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (subRes.ok) setBillingData(await subRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create subscription');
      }
    } catch (e) { toast.error('Connection error'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading billing data...</div>;

  const summary = billingData?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Billing & Subscriptions</h2>
        <Button onClick={() => setShowAddSub(true)} className="bg-orange-500 hover:bg-orange-600 rounded-xl gap-2" data-testid="add-subscription-btn">
          <Plus className="w-4 h-4" /> Add Subscription
        </Button>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Subs', val: summary.active || 0, color: '#10B981' },
          { label: 'Trial', val: summary.trial || 0, color: '#F59E0B' },
          { label: 'Monthly MRR', val: `$${(summary.total_mrr || 0).toLocaleString()}`, color: '#E86C3D' },
          { label: 'Est. ARR', val: `$${(summary.total_arr || 0).toLocaleString()}`, color: '#8B5CF6' },
        ].map(kpi => (
          <Card key={kpi.label} className="p-4 rounded-2xl text-center bg-white">
            <p className="text-2xl font-black" style={{ color: kpi.color }}>{kpi.val}</p>
            <p className="text-xs text-gray-500 font-medium mt-1">{kpi.label}</p>
          </Card>
        ))}
      </div>

      {/* Subscriptions Table */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-gray-900 mb-4">Active Subscriptions</h3>
        {billingData?.subscriptions?.length > 0 ? (
          <div className="space-y-3">
            {billingData.subscriptions.map(sub => (
              <div key={sub.id} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                  {sub.org_name?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm">{sub.org_name}</p>
                  <p className="text-xs text-gray-500">{sub.plan?.toUpperCase()} · {sub.billing_cycle} · {sub.seats} seats</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">${sub.price?.toLocaleString()}/{sub.billing_cycle === 'annual' ? 'yr' : 'mo'}</p>
                  <Badge className={sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {sub.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No subscriptions yet. Add your first client subscription.</p>
          </div>
        )}
      </Card>

      {/* Plan Cards */}
      {plans && (
        <Card className="p-5 rounded-2xl bg-white">
          <h3 className="font-bold text-sm text-gray-900 mb-4">Available Plans</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(plans.plans).map(([key, plan]) => (
              <div key={key} className={`p-5 rounded-2xl border-2 ${key === 'professional' ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                {key === 'professional' && <Badge className="bg-orange-500 text-white mb-2">Popular</Badge>}
                <h4 className="font-bold text-lg">{plan.name}</h4>
                <p className="text-2xl font-black text-gray-900 mt-2">${plan.price_monthly}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <p className="text-xs text-gray-500 mb-3">or ${plan.price_annual}/yr (save {Math.round((1 - plan.price_annual / (plan.price_monthly * 12)) * 100)}%)</p>
                <ul className="text-xs space-y-1.5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-400 mt-3">Up to {plan.max_seats} seats</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Add Subscription Dialog */}
      <Dialog open={showAddSub} onOpenChange={setShowAddSub}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="text-gray-900">Add Subscription</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700">Organization</Label>
              <Select value={newSub.org_id} onValueChange={v => setNewSub({...newSub, org_id: v})}>
                <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="Select org" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700">Plan</Label>
              <Select value={newSub.plan} onValueChange={v => setNewSub({...newSub, plan: v})}>
                <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="starter">Starter - $299/mo</SelectItem>
                  <SelectItem value="professional">Professional - $599/mo</SelectItem>
                  <SelectItem value="enterprise">Enterprise - $999/mo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700">Billing Cycle</Label>
              <Select value={newSub.billing_cycle} onValueChange={v => setNewSub({...newSub, billing_cycle: v})}>
                <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual (Save ~17%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700">Seats</Label>
              <Input type="number" value={newSub.seats} onChange={e => setNewSub({...newSub, seats: parseInt(e.target.value) || 50})} className="bg-white border-gray-300" />
            </div>
            <Button onClick={createSubscription} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl">
              {saving ? 'Creating...' : 'Create Subscription'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Feature Access Control Component
const FeatureAccessCard = () => {
  const [programs, setPrograms] = useState([]);
  const [selectedProg, setSelectedProg] = useState(null);
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProgs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/programs`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (res.ok) {
          const data = await res.json();
          setPrograms(data.programs || data || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchProgs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFeatures = async (progId) => {
    setSelectedProg(progId);
    try {
      const res = await fetch(`${API_URL}/api/platform/programs/${progId}/features`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setFeatures(await res.json());
    } catch (e) { console.error(e); }
  };

  const toggleFeature = async (feature, current) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/programs/${selectedProg}/features`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [feature]: !current })
      });
      if (res.ok) {
        setFeatures(prev => ({ ...prev, [feature]: !current }));
        toast.success('Feature updated!');
      }
    } catch (e) { toast.error('Failed'); }
    setSaving(false);
  };

  const FEATURE_LABELS = {
    checkins: 'Weekly Check-ins', goals: 'SMART Goals', wins_wall: 'Wins Wall',
    courses: 'Courses', impact_projects: 'Impact Projects', ai_guide: 'AI Career Guide',
    assessments: 'Skills Assessments', career_exploration: 'Career Exploration', events: 'Events & Orientation',
    inbox_messaging: 'Inbox Messaging', helpdesk_qa: 'Help Desk Q&A', parent_portal: 'Parent Portal',
    leaderboard: 'Leaderboard', badges: 'Badges & Achievements', trivia: 'Weekly Trivia',
    resources: 'Resource Center', pdf_reports: 'PDF Reports'
  };

  if (loading) return null;

  return (
    <Card className="p-6 rounded-2xl bg-white" data-testid="feature-access-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
          <Shield className="w-6 h-6 text-violet-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Feature Access Control</h3>
          <p className="text-sm text-gray-500">Enable/disable features per program (all ON by default)</p>
        </div>
      </div>

      <div className="mb-4">
        <Select value={selectedProg || ''} onValueChange={loadFeatures}>
          <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder="Select a program to configure" /></SelectTrigger>
          <SelectContent className="bg-white">
            {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.display_name || p.name} ({p.program_type})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {features && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(FEATURE_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <button
                onClick={() => toggleFeature(key, features[key])}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${features[key] ? 'bg-green-500' : 'bg-gray-300'}`}
                data-testid={`feature-${key}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${features[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Digest Settings Component
const DigestSettingsCard = () => {
  const [digestData, setDigestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/digest-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) setDigestData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDigest = async (programId, field, currentValue) => {
    setSaving(prev => ({ ...prev, [programId + field]: true }));
    try {
      const res = await fetch(`${API_URL}/api/platform/digest-settings/${programId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: !currentValue })
      });
      if (res.ok) {
        toast.success(`Setting updated!`);
        // Refresh
        const r2 = await fetch(`${API_URL}/api/platform/digest-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (r2.ok) setDigestData(await r2.json());
      }
    } catch (e) { toast.error('Failed to update'); }
    setSaving(prev => ({ ...prev, [programId + field]: false }));
  };

  if (loading) return <Card className="p-6 rounded-2xl bg-white"><p className="text-gray-400 text-center">Loading digest settings...</p></Card>;

  return (
    <Card className="p-6 rounded-2xl bg-white" data-testid="digest-settings-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
          <Mail className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Weekly Digest & Notifications</h3>
          <p className="text-sm text-gray-500">Control automated email digests and question notifications per program</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
        <p className="text-xs text-amber-800">
          <strong>Weekly Digest:</strong> OFF by default — enable per program to send automated Monday 9am EST emails.<br/>
          <strong>Q&A Nudge:</strong> ON by default — sends daily 6pm EST reminders to monitors with unanswered questions (weekdays only).
        </p>
      </div>

      {digestData?.organizations?.length > 0 ? (
        <div className="space-y-5">
          {digestData.organizations.map(org => (
            <div key={org.org_id} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <p className="font-bold text-sm text-gray-900">{org.org_name}</p>
              </div>
              {org.programs.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {org.programs.map(prog => (
                    <div key={prog.program_id} className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900">{prog.program_name}</p>
                        <p className="text-xs text-gray-500">{prog.program_type?.toUpperCase()}</p>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Weekly Digest</span>
                          <button
                            onClick={() => toggleDigest(prog.program_id, 'weekly_digest_enabled', prog.weekly_digest_enabled)}
                            disabled={saving[prog.program_id + 'weekly_digest_enabled']}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prog.weekly_digest_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                            data-testid={`toggle-digest-${prog.program_id}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${prog.weekly_digest_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Q&A Emails</span>
                          <button
                            onClick={() => toggleDigest(prog.program_id, 'question_notifications_enabled', prog.question_notifications_enabled)}
                            disabled={saving[prog.program_id + 'question_notifications_enabled']}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prog.question_notifications_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                            data-testid={`toggle-qa-${prog.program_id}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${prog.question_notifications_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">6pm Nudge</span>
                          <button
                            onClick={() => toggleDigest(prog.program_id, 'question_nudge_enabled', prog.question_nudge_enabled)}
                            disabled={saving[prog.program_id + 'question_nudge_enabled']}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prog.question_nudge_enabled !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                            data-testid={`toggle-nudge-${prog.program_id}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${prog.question_nudge_enabled !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-3 text-sm text-gray-400">No programs</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-400 py-4">No organizations found</p>
      )}
    </Card>
  );
};

// Activity Tracker Component
const ActivityTracker = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await window.fetch(`${API_URL}/api/admin/user-activity`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading activity data...</div>;
  if (!data) return <div className="text-center py-12 text-gray-400">Failed to load activity data</div>;

  const s = data.summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Activity Tracker</h2>
          <p className="text-sm text-gray-500">Track who has logged in, changed passwords, and updated profiles</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.location.href = '/admin/completion-tracker'} className="bg-violet-600 hover:bg-violet-700 rounded-xl text-xs gap-1">
            <CheckCircle className="w-3.5 h-3.5" /> Completion Tracker
          </Button>
          <Button onClick={() => window.location.href = '/admin/analytics'} className="bg-orange-500 hover:bg-orange-600 rounded-xl text-xs gap-1">
            <BarChart3 className="w-3.5 h-3.5" /> Visual Analytics
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: 'Total Users', v: s.total_users, c: '#3B82F6' },
          { l: 'Logged In', v: s.logged_in, c: '#10B981' },
          { l: 'Never Logged In', v: s.never_logged_in, c: '#EF4444' },
          { l: 'Changed Password', v: s.changed_password, c: '#8B5CF6' },
          { l: 'Login Rate', v: `${s.login_rate}%`, c: '#E86C3D' },
        ].map(k => (
          <Card key={k.l} className="p-4 rounded-2xl bg-white text-center">
            <p className="text-2xl font-black" style={{ color: k.c }}>{k.v}</p>
            <p className="text-xs text-gray-500">{k.l}</p>
          </Card>
        ))}
      </div>

      {/* By Role */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-gray-900 mb-3">Login Status by Role</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {Object.entries(data.by_role || {}).map(([role, stats]) => (
            <div key={role} className="p-3 rounded-xl bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <Badge variant="outline" className="text-xs">{role}</Badge>
                <span className="text-xs font-bold text-gray-600">{stats.logged_in}/{stats.total}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: `${stats.total > 0 ? (stats.logged_in / stats.total * 100) : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent Logins */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-gray-900 mb-3">Recent Logins</h3>
        {data.recent_logins?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 text-xs text-gray-600">
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-center">Role</th>
                <th className="p-3 text-center">Logins</th>
                <th className="p-3 text-center">PW Changed</th>
                <th className="p-3 text-center">Profile</th>
                <th className="p-3 text-center">Last Login</th>
              </tr></thead>
              <tbody>
                {data.recent_logins.map((u, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-3 font-semibold">{u.name}</td>
                    <td className="p-3 text-gray-500 text-xs">{u.email}</td>
                    <td className="p-3 text-center"><Badge variant="outline" className="text-[10px]">{u.role}</Badge></td>
                    <td className="p-3 text-center font-bold">{u.login_count || 0}</td>
                    <td className="p-3 text-center">{u.changed_password ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                    <td className="p-3 text-center">{(u.has_interests || u.has_school) ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                    <td className="p-3 text-center text-xs text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-6 text-gray-400">No logins recorded yet</p>
        )}
      </Card>

      {/* Never Logged In */}
      <Card className="p-5 rounded-2xl bg-white">
        <h3 className="font-bold text-sm text-red-600 mb-3">Never Logged In ({data.never_logged_in?.length || 0})</h3>
        {data.never_logged_in?.length > 0 ? (
          <div className="max-h-64 overflow-y-auto space-y-1">
            {data.never_logged_in.map((u, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-red-50 text-sm">
                <div>
                  <span className="font-semibold text-gray-900">{u.name}</span>
                  <span className="text-gray-400 text-xs ml-2">{u.email}</span>
                </div>
                <Badge className="bg-red-100 text-red-600 text-[10px]">{u.role}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-4 text-green-600 font-semibold">Everyone has logged in!</p>
        )}
      </Card>
    </div>
  );
};

const PlatformAdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View As / Impersonate
  const [showViewAs, setShowViewAs] = useState(false);
  const [orgAdmins, setOrgAdmins] = useState([]);
  const [viewAsLoading, setViewAsLoading] = useState(false);

  const loadOrgAdmins = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/org-admins`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setOrgAdmins(d.admins || []); }
    } catch (e) { console.error(e); }
  };

  const viewAsUser = async (targetUserId) => {
    setViewAsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/view-as/${targetUserId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Store original admin token for return
        localStorage.setItem('original_admin_token', localStorage.getItem('token'));
        localStorage.setItem('impersonating', 'true');
        // Switch to impersonated user
        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success(`Now viewing as ${data.user.name} (${data.user.role})`);
        navigate('/admin');
        window.location.reload();
      }
    } catch (e) { toast.error('Failed to impersonate'); }
    setViewAsLoading(false);
  };

  // Create Program Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    slug: '',
    city: '',
    state: '',
    organization: '',
    display_name: ''
  });
  const [creating, setCreating] = useState(false);

  // Onboard Organization Modal (Full wizard)
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    org_name: '', program_name: '', program_type: 'syep',
    city: '', state: '', admin_name: '', admin_email: '',
    season_name: '2025-2026', theme: 'cashapp_clean'
  });
  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);

  const handleOnboard = async () => {
    for (const field of ['org_name', 'program_name', 'city', 'state', 'admin_name', 'admin_email']) {
      if (!onboardForm[field]) { toast.error(`Please fill in ${field.replace('_', ' ')}`); return; }
    }
    setOnboarding(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/platform/onboard`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(onboardForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOnboardResult(data);
        toast.success(`${data.organization.name} onboarded successfully!`);
        fetchData();
      } else {
        toast.error(data.detail || 'Onboarding failed');
      }
    } catch (e) { toast.error('Connection error'); }
    setOnboarding(false);
  };

  // Invite User Modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'program_admin',
    program_id: '',
    work_site: '',
    send_email: true
  });
  const [inviting, setInviting] = useState(false);
  const [lastInviteResult, setLastInviteResult] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // User Management
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programUsers, setProgramUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null); // NEW: Role filtering

  // Season Management
  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [showNewSeasonForm, setShowNewSeasonForm] = useState(false);
  const [newSeasonForm, setNewSeasonForm] = useState({
    season_name: '',
    year: new Date().getFullYear(),
    start_date: '',
    end_date: ''
  });
  const [creatingNewSeason, setCreatingNewSeason] = useState(false);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [programsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/programs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/platform/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (programsRes.ok) {
        const programsData = await programsRes.json();
        setPrograms(programsData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramUsers = async (programId) => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProgramUsers(data);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchProgramSeasons = async (programId) => {
    setLoadingSeasons(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}/seasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSeasons(data.seasons || []);
      }
    } catch (error) {
      toast.error('Failed to load seasons');
    } finally {
      setLoadingSeasons(false);
    }
  };

  const startNewSeason = async () => {
    if (!newSeasonForm.season_name || !newSeasonForm.year) {
      toast.error('Please fill in season name and year');
      return;
    }

    if (!window.confirm(`⚠️ Starting a new season will:\n\n• Reset all youth points to 0\n• Reset onboarding progress\n• Archive current goals\n• Preserve historical data\n\nAre you sure you want to start "${newSeasonForm.season_name}"?`)) {
      return;
    }

    setCreatingNewSeason(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${selectedProgram.id}/seasons/new`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSeasonForm)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message);
        setShowNewSeasonForm(false);
        setNewSeasonForm({
          season_name: '',
          year: new Date().getFullYear(),
          start_date: '',
          end_date: ''
        });
        fetchProgramSeasons(selectedProgram.id);
        fetchData();
      } else {
        throw new Error(result.detail || 'Failed to start new season');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingNewSeason(false);
    }
  };

  const runMigration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/migrate-to-multitenant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Migration complete! Updated ${result.users_updated} users, ${result.goals_updated} goals, ${result.projects_updated} projects`);
        fetchData();
      } else {
        throw new Error('Migration failed');
      }
    } catch (error) {
      toast.error('Migration failed');
    }
  };

  const createProgram = async () => {
    if (!createForm.name || !createForm.slug || !createForm.city || !createForm.state || !createForm.organization) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(createForm)
      });

      if (response.ok) {
        toast.success('Program created successfully!');
        setShowCreateModal(false);
        setCreateForm({ name: '', slug: '', city: '', state: '', organization: '', display_name: '' });
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create program');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const inviteUser = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Program admins need a program assigned (or we skip for initial setup)
    if (inviteForm.role !== 'program_admin' && !inviteForm.program_id) {
      toast.error('Please select a program for this user');
      return;
    }

    setInviting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteForm)
      });

      const result = await response.json();

      if (response.ok) {
        setLastInviteResult(result);
        toast.success(`User ${inviteForm.name} invited successfully!`);
        
        // Refresh data
        fetchData();
        if (selectedProgram) {
          fetchProgramUsers(selectedProgram.id);
        }
      } else {
        throw new Error(result.detail || 'Failed to invite user');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkImporting(true);
    setBulkResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/admin/import-users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setBulkResult(data);
        toast.success(`Imported ${data.success.length} users (${data.errors.length} errors)`);
        if (selectedProgram) fetchProgramUsers(selectedProgram.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (err) {
      toast.error('Import failed');
    }
    setBulkImporting(false);
    e.target.value = '';
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        if (selectedProgram) {
          fetchProgramUsers(selectedProgram.id);
        }
        fetchData();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const testEmailIntegration = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/test-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(`Test email sent to ${result.email_sent_to}!`);
      } else {
        toast.error(result.detail || 'Email test failed');
      }
    } catch (error) {
      toast.error('Failed to test email');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'platform_admin': 'bg-purple-500/20 text-purple-400',
      'program_admin': 'bg-blue-500/20 text-blue-400',
      'admin': 'bg-indigo-500/20 text-indigo-400',
      'coach': 'bg-green-500/20 text-green-400',
      'lead_monitor': 'bg-yellow-500/20 text-yellow-400',
      'youth': 'bg-orange-500/20 text-orange-400',
      'parent': 'bg-pink-500/20 text-pink-400'
    };
    return colors[role] || 'bg-slate-500/20 text-slate-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.surface }}>
      {/* Header */}
      <header 
        className="border-b sticky top-0 z-50 backdrop-blur-md"
        style={{ 
          backgroundColor: colors.header,
          borderColor: colors.border 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: colors.primary }}
            >
              <Globe className="w-6 h-6" style={{ color: colors.text_on_primary }} />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: colors.text_on_primary }}>YAS CONNECT</h1>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>Platform Administration</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => { setShowViewAs(true); loadOrgAdmins(); }}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs gap-1"
              data-testid="view-as-btn"
            >
              <Eye className="w-3.5 h-3.5" /> View As Client
            </Button>
            <div className="text-right">
              <p className="text-sm font-medium" style={{ color: colors.text_on_primary }}>{user?.name}</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>Platform Admin</p>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                style={{ color: 'rgba(255,255,255,0.7)' }}
                className="hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="p-4 rounded-2xl"
            style={{ 
              backgroundColor: colors.card,
              borderColor: colors.border 
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}
              >
                <Building2 className="w-6 h-6" style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold" 
                  style={{ color: colors.text }}
                  data-testid="total-programs"
                >
                  {stats?.total_programs || 0}
                </p>
                <p className="text-xs" style={{ color: colors.text_muted }}>Programs</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className="p-4 rounded-2xl"
            style={{ 
              backgroundColor: colors.card,
              borderColor: colors.border 
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}
              >
                <Users className="w-6 h-6" style={{ color: '#22C55E' }} />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold" 
                  style={{ color: colors.text }}
                  data-testid="total-youth"
                >
                  {stats?.total_youth || 0}
                </p>
                <p className="text-xs" style={{ color: colors.text_muted }}>Total Youth</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className="p-4 rounded-2xl"
            style={{ 
              backgroundColor: colors.card,
              borderColor: colors.border 
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}
              >
                <BarChart3 className="w-6 h-6" style={{ color: '#A855F7' }} />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold" 
                  style={{ color: colors.text }}
                  data-testid="total-goals"
                >
                  {stats?.total_goals || 0}
                </p>
                <p className="text-xs" style={{ color: colors.text_muted }}>Goals Created</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className="p-4 rounded-2xl"
            style={{ 
              backgroundColor: colors.card,
              borderColor: colors.border 
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(249,115,22,0.1)' }}
              >
                <CheckCircle className="w-6 h-6" style={{ color: '#F97316' }} />
              </div>
              <div>
                <p 
                  className="text-2xl font-bold" 
                  style={{ color: colors.text }}
                  data-testid="total-projects"
                >
                  {stats?.total_projects_submitted || 0}
                </p>
                <p className="text-xs" style={{ color: colors.text_muted }}>Projects Submitted</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-gray-200 mb-6 rounded-xl p-1 overflow-x-auto flex-nowrap w-full justify-start" style={{ scrollbarWidth: 'none' }}>
            <TabsTrigger value="overview" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="programs" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="programs-tab">
              <Building2 className="w-4 h-4 mr-2" /> Organizations
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="billing" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="billing-tab">
              <TrendingUp className="w-4 h-4 mr-2" /> Billing
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="activity-tab">
              <Users className="w-4 h-4 mr-2" /> Activity
            </TabsTrigger>
            <TabsTrigger value="demos" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" /> Demos
            </TabsTrigger>
            <TabsTrigger value="assessments" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Assessments
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg flex-shrink-0 whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab — Cross-Org Analytics */}
          <TabsContent value="overview">
            <CrossOrgAnalytics navigate={navigate} />
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Programs</h2>
              <div className="flex gap-2">
                <Button
                  onClick={runMigration}
                  variant="outline"
                  style={{ 
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.card
                  }}
                  className="hover:opacity-80"
                  data-testid="run-migration-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Migration
                </Button>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="add-program-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Program
                </Button>
                <Button
                  onClick={() => { setOnboardResult(null); setOnboardForm({ org_name: '', program_name: '', program_type: 'syep', city: '', state: '', admin_name: '', admin_email: '', season_name: '2025-2026', theme: 'cashapp_clean' }); setShowOnboardModal(true); }}
                  className="bg-[#C4542D] hover:bg-[#8B3520] text-white"
                  data-testid="onboard-org-btn"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Onboard Organization
                </Button>
              </div>
            </div>

            {programs.length === 0 ? (
              <Card 
                className="p-12 rounded-3xl text-center"
                style={{ 
                  backgroundColor: colors.card,
                  borderColor: colors.border 
                }}
              >
                <Building2 className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text_muted }} />
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>No Programs Yet</h3>
                <p className="mb-6" style={{ color: colors.text_secondary }}>
                  Click "Run Migration" to sync data, or "Add Program" to create a new one.
                </p>
                <Button onClick={runMigration} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Migration
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {programs.map((program) => (
                  <motion.div
                    key={program.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card 
                      className="p-5 rounded-2xl hover:shadow-lg transition-all cursor-pointer"
                      style={{ 
                        backgroundColor: colors.card,
                        borderColor: colors.border 
                      }}
                      onClick={() => navigate(`/admin/program/${program.id}`)}
                      data-testid={`program-card-${program.slug}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
                            style={{ backgroundColor: program.branding?.primary_color || '#E86C3D' }}
                          >
                            🏙️
                          </div>
                          <div>
                            <h3 className="font-bold" style={{ color: colors.text }}>{program.name}</h3>
                            <p className="text-sm" style={{ color: colors.text_secondary }}>
                              {program.city}, {program.state}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          className={`${
                            program.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {program.status}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div 
                          className="rounded-xl p-3 text-center"
                          style={{ 
                            backgroundColor: colors.surface,
                            border: `1px solid ${colors.border}`
                          }}
                        >
                          <p className="text-xl font-bold" style={{ color: colors.text }}>
                            {program.participant_count || 0}
                          </p>
                          <p className="text-xs" style={{ color: colors.text_muted }}>Youth</p>
                        </div>
                        <div 
                          className="rounded-xl p-3 text-center"
                          style={{ 
                            backgroundColor: colors.surface,
                            border: `1px solid ${colors.border}`
                          }}
                        >
                          <p className="text-xl font-bold" style={{ color: colors.text }}>
                            {program.staff_count || 0}
                          </p>
                          <p className="text-xs" style={{ color: colors.text_muted }}>Staff</p>
                        </div>
                      </div>

                      <div 
                        className="flex items-center justify-between text-sm mb-3"
                        style={{ color: colors.text_secondary }}
                      >
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {program.seasons?.[program.seasons.length - 1]?.season_name || 'No season'}
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          style={{ 
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.card
                          }}
                          className="flex-1 hover:opacity-80"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProgram(program);
                            setActiveTab('users');
                            fetchProgramUsers(program.id);
                          }}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Users
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          style={{ 
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.card
                          }}
                          className="flex-1 hover:opacity-80"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProgram(program);
                            fetchProgramSeasons(program.id);
                            setShowSeasonModal(true);
                          }}
                          data-testid={`manage-seasons-${program.slug}`}
                        >
                          <History className="w-4 h-4 mr-1" />
                          Seasons
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-[#1B2D4F] hover:bg-[#152440] text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/platform/org/${program.organization_id}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Org Detail
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Program Selector and Actions */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold" style={{ color: colors.text }}>User Management</h2>
                  <Select 
                    value={selectedProgram?.id || ''} 
                    onValueChange={(value) => {
                      const program = programs.find(p => p.id === value);
                      setSelectedProgram(program);
                      if (program) fetchProgramUsers(program.id);
                    }}
                  >
                    <SelectTrigger 
                      className="w-[250px]"
                      style={{
                        backgroundColor: colors.card,
                        borderColor: colors.border,
                        color: colors.text
                      }}
                      data-testid="program-selector"
                    >
                      <SelectValue placeholder="Select a program" />
                    </SelectTrigger>
                    <SelectContent 
                      style={{
                        backgroundColor: colors.card,
                        borderColor: colors.border
                      }}
                    >
                      {programs.map((program) => (
                        <SelectItem 
                          key={program.id} 
                          value={program.id}
                          style={{ color: colors.text }}
                          className="hover:opacity-80"
                        >
                          {program.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={() => {
                    setInviteForm({
                      email: '',
                      name: '',
                      role: 'program_admin',
                      program_id: selectedProgram?.id || '',
                      work_site: '',
                      send_email: true
                    });
                    setLastInviteResult(null);
                    setShowInviteModal(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="invite-user-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
                <div className="relative">
                  <input type="file" accept=".csv" onChange={handleBulkImport} className="hidden" id="csv-import-input" data-testid="csv-import-input" />
                  <Button
                    onClick={() => document.getElementById('csv-import-input')?.click()}
                    variant="outline"
                    disabled={bulkImporting}
                    data-testid="bulk-import-btn"
                  >
                    {bulkImporting ? 'Importing...' : 'Import CSV'}
                  </Button>
                </div>
              </div>

              {/* Bulk Import Results */}
              {bulkResult && (
                <Card className="p-4 rounded-2xl mb-4" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-sm" style={{ color: colors.text }}>Import Results</h4>
                    <button onClick={() => setBulkResult(null)} className="text-xs text-muted-foreground hover:text-foreground">&times; Close</button>
                  </div>
                  <div className="flex gap-4 text-sm mb-2">
                    <span style={{ color: '#10B981' }}>Imported: {bulkResult.success?.length || 0}</span>
                    <span style={{ color: '#EF4444' }}>Errors: {bulkResult.errors?.length || 0}</span>
                    <span style={{ color: colors.text_secondary }}>Total rows: {bulkResult.total || 0}</span>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1 mt-2">
                      {bulkResult.errors.map((err, i) => (
                        <p key={i} className="text-red-500">Row {err.row}: {err.error}</p>
                      ))}
                    </div>
                  )}
                  {bulkResult.success?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer" style={{ color: colors.text_secondary }}>Show {bulkResult.success.length} imported accounts (with passwords)</summary>
                      <div className="max-h-40 overflow-y-auto text-xs mt-1 space-y-1 font-mono">
                        {bulkResult.success.map((u, i) => (
                          <p key={i} style={{ color: colors.text }}>{u.name} — {u.email} — {u.password} ({u.role})</p>
                        ))}
                      </div>
                    </details>
                  )}
                </Card>
              )}

              {/* User List */}
              {!selectedProgram ? (
                <Card 
                  className="p-12 rounded-3xl text-center"
                  style={{
                    backgroundColor: colors.card,
                    borderColor: colors.border
                  }}
                >
                  <Users className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text_muted }} />
                  <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>Select a Program</h3>
                  <p style={{ color: colors.text_secondary }}>
                    Choose a program from the dropdown above to manage its users.
                  </p>
                </Card>
              ) : loadingUsers ? (
                <Card 
                  className="p-12 rounded-3xl text-center"
                  style={{
                    backgroundColor: colors.card,
                    borderColor: colors.border
                  }}
                >
                  <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                    {Object.entries(programUsers?.by_role || {}).map(([role, users]) => (
                      <Card 
                        key={role} 
                        className={`p-3 rounded-xl text-center cursor-pointer transition-all hover:shadow-md ${
                          selectedRoleFilter === role ? 'ring-2 ring-blue-500' : ''
                        }`}
                        style={{
                          backgroundColor: selectedRoleFilter === role ? 'rgba(59,130,246,0.1)' : colors.card,
                          borderColor: selectedRoleFilter === role ? '#3B82F6' : colors.border
                        }}
                        onClick={() => setSelectedRoleFilter(selectedRoleFilter === role ? null : role)}
                      >
                        <p className="text-lg font-bold" style={{ color: colors.text }}>{users.length}</p>
                        <p className="text-xs capitalize" style={{ color: colors.text_muted }}>{role.replace('_', ' ')}s</p>
                      </Card>
                    ))}
                  </div>

                  {/* Filter Indicator */}
                  {selectedRoleFilter && (
                    <div className="flex items-center justify-between mb-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.05)', borderLeft: '3px solid #3B82F6' }}>
                      <span className="text-sm" style={{ color: colors.text }}>
                        Showing: <strong className="capitalize">{selectedRoleFilter.replace('_', ' ')}s</strong>
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedRoleFilter(null)}
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear Filter
                      </Button>
                    </div>
                  )}

                  {/* User Table */}
                  <Card 
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.card,
                      borderColor: colors.border
                    }}
                  >
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead style={{ backgroundColor: colors.surface }}>
                          <tr>
                            <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Name</th>
                            <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Email</th>
                            <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Role</th>
                            <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Points</th>
                            <th className="text-right p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedRoleFilter 
                            ? programUsers?.users?.filter(u => u.role === selectedRoleFilter)
                            : programUsers?.users
                          )?.map((u) => (
                            <tr 
                              key={u.id}
                              className="hover:opacity-80"
                              style={{ borderTop: `1px solid ${colors.border}` }}
                            >
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{u.avatar_emoji || '😊'}</span>
                                  <span className="font-medium" style={{ color: colors.text }}>{u.name}</span>
                                </div>
                              </td>
                              <td className="p-4" style={{ color: colors.text }}>{u.email}</td>
                              <td className="p-4">
                                <Badge 
                                  className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0"
                                >
                                  {u.role.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="p-4 font-medium" style={{ color: colors.text }}>{u.total_points || 0}</td>
                              <td className="p-4 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteUser(u.id, u.name)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingSection />
          </TabsContent>

          {/* Activity Tracking Tab */}
          <TabsContent value="activity">
            <ActivityTracker />
          </TabsContent>


          {/* Assessments Tab */}
          <TabsContent value="assessments">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Assessment Builder</h2>
                <Button onClick={() => window.location.href = '/admin/assessment-builder'} className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl">
                  Open Builder
                </Button>
              </div>
              <Card className="p-6 rounded-2xl cursor-pointer hover:shadow-lg transition-all" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', borderColor: colors.border }} onClick={() => window.location.href = '/admin/assessment-builder'}>
                <div className="text-white">
                  <h3 className="text-lg font-bold mb-2">AI Assessment Builder</h3>
                  <p className="text-white/70 text-sm mb-4">Create custom assessments with AI assistance. Define topics, question types, scoring criteria — AI generates the questions.</p>
                  <div className="flex gap-3 text-xs text-white/60">
                    <span>Custom Questions</span>
                    <span>AI-Generated</span>
                    <span>Auto-Scoring</span>
                    <span>Youth Analytics</span>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Platform Settings</h2>

              {/* Email Integration */}
              <Card 
                className="p-6 rounded-2xl"
                style={{
                  backgroundColor: colors.card,
                  borderColor: colors.border
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}
                  >
                    <Mail className="w-6 h-6" style={{ color: '#3B82F6' }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text }}>Email Integration</h3>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Configure email sending for user invitations</p>
                  </div>
                </div>

                <div 
                  className="rounded-xl p-4 mb-4"
                  style={{
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #FCD34D'
                  }}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#D97706' }} />
                    <div>
                      <p className="font-medium" style={{ color: '#92400E' }}>Domain Verification Required</p>
                      <p className="text-sm mt-1" style={{ color: '#78350F' }}>
                        To send emails to any address, you need to verify a domain at{' '}
                        <a 
                          href="https://resend.com/domains" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="underline font-medium"
                          style={{ color: '#1D4ED8' }}
                        >
                          resend.com/domains
                        </a>
                        . Currently, emails can only be sent to <strong style={{ color: '#92400E' }}>earlbrownedu@gmail.com</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={testEmailIntegration}
                  variant="outline"
                  style={{
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.card
                  }}
                  className="hover:opacity-80"
                  data-testid="test-email-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
              </Card>

              {/* Weekly Digest Settings */}
              <DigestSettingsCard />

              {/* Feature Access Control */}
              <FeatureAccessCard />

              {/* Migration Tool */}
              <Card 
                className="p-6 rounded-2xl"
                style={{
                  backgroundColor: colors.card,
                  borderColor: colors.border
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}
                  >
                    <RefreshCw className="w-6 h-6" style={{ color: '#A855F7' }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text }}>Data Migration</h3>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Run migration to set up multi-tenant data structure</p>
                  </div>
                </div>

                <Button
                  onClick={runMigration}
                  variant="outline"
                  style={{
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.card
                  }}
                  className="hover:opacity-80"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Migration
                </Button>
              </Card>

              {/* Theme Configuration */}
              <Card 
                className="p-6 rounded-2xl"
                style={{
                  backgroundColor: colors.card,
                  borderColor: colors.border
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}
                  >
                    <Settings className="w-6 h-6" style={{ color: '#22C55E' }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text }}>Theme & Branding</h3>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Customize design templates, colors, and logos for programs</p>
                  </div>
                </div>

                <Button
                  onClick={() => window.location.href = '/admin/theme'}
                  variant="outline"
                  style={{
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.card
                  }}
                  className="hover:opacity-80"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Theme
                </Button>
              </Card>

              {/* Agreement Analytics */}
              <Card 
                className="p-6 rounded-2xl"
                style={{
                  backgroundColor: colors.card,
                  borderColor: colors.border
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}
                  >
                    <Shield className="w-6 h-6" style={{ color: '#A855F7' }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text }}>Agreement Analytics</h3>
                    <p className="text-sm" style={{ color: colors.text_secondary }}>Monitor agreement signing and parent consent status</p>
                  </div>
                </div>

                <Button
                  onClick={() => window.location.href = '/admin/agreements'}
                  variant="outline"
                  style={{
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.card
                  }}
                  className="hover:opacity-80"
                  data-testid="agreement-analytics-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  View Analytics
                </Button>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Program Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Create New Program</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Program Name *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => {
                    setCreateForm({ 
                      ...createForm, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    });
                  }}
                  placeholder="e.g., Philadelphia Youth Works"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                  data-testid="program-name-input"
                />
              </div>

              <div>
                <Label className="text-slate-300">Slug (URL-friendly) *</Label>
                <Input
                  value={createForm.slug}
                  onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                  placeholder="e.g., philly_youth_works"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                  data-testid="program-slug-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">City *</Label>
                  <Input
                    value={createForm.city}
                    onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                    placeholder="Philadelphia"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    data-testid="program-city-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">State *</Label>
                  <Input
                    value={createForm.state}
                    onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                    placeholder="PA"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    data-testid="program-state-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Organization *</Label>
                <Input
                  value={createForm.organization}
                  onChange={(e) => setCreateForm({ ...createForm, organization: e.target.value })}
                  placeholder="City of Philadelphia"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                  data-testid="program-org-input"
                />
              </div>

              <div>
                <Label className="text-slate-300">Display Name (optional)</Label>
                <Input
                  value={createForm.display_name}
                  onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                  placeholder="Youth Works CONNECT"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Defaults to &quot;[Program Name] CONNECT&quot;
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createProgram}
                  disabled={creating}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="create-program-submit"
                >
                  {creating ? 'Creating...' : 'Create Program'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite User Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Invite User
              </DialogTitle>
            </DialogHeader>
            
            {lastInviteResult ? (
              <div className="space-y-4 mt-4">
                <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="font-medium text-green-400">User Invited Successfully!</span>
                  </div>
                  <p className="text-sm text-slate-300">
                    {lastInviteResult.user.name} ({lastInviteResult.user.email})
                  </p>
                </div>

                <div className="bg-slate-900/50 rounded-xl p-4">
                  <Label className="text-slate-400 text-sm">Temporary Password</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 bg-slate-800 px-3 py-2 rounded-lg text-white font-mono">
                      {lastInviteResult.temp_password}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(lastInviteResult.temp_password)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {lastInviteResult.email_sent?.status === 'success' ? (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-400">Welcome email sent to {lastInviteResult.user.email}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-yellow-400">
                        Email not sent (share the password manually)
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setLastInviteResult(null);
                      setInviteForm({
                        ...inviteForm,
                        email: '',
                        name: '',
                        work_site: ''
                      });
                    }}
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    Invite Another
                  </Button>
                  <Button
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Full Name *</Label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="John Smith"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    data-testid="invite-name-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Email Address *</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                    data-testid="invite-email-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Role *</Label>
                  <Select 
                    value={inviteForm.role} 
                    onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="program_admin" className="text-white hover:bg-slate-700">Program Admin</SelectItem>
                      <SelectItem value="admin" className="text-white hover:bg-slate-700">Admin</SelectItem>
                      <SelectItem value="coach" className="text-white hover:bg-slate-700">Coach</SelectItem>
                      <SelectItem value="lead_monitor" className="text-white hover:bg-slate-700">Lead Monitor</SelectItem>
                      <SelectItem value="youth" className="text-white hover:bg-slate-700">Youth</SelectItem>
                      <SelectItem value="parent" className="text-white hover:bg-slate-700">Parent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300">Program {inviteForm.role !== 'program_admin' ? '*' : '(optional for Program Admins)'}</Label>
                  <Select 
                    value={inviteForm.program_id} 
                    onValueChange={(value) => setInviteForm({ ...inviteForm, program_id: value })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="invite-program-select">
                      <SelectValue placeholder="Select a program" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {programs.map((program) => (
                        <SelectItem key={program.id} value={program.id} className="text-white hover:bg-slate-700">
                          {program.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {inviteForm.role === 'youth' && (
                  <div>
                    <Label className="text-slate-300">Work Site</Label>
                    <Input
                      value={inviteForm.work_site}
                      onChange={(e) => setInviteForm({ ...inviteForm, work_site: e.target.value })}
                      placeholder="e.g., City Hall"
                      className="bg-slate-900 border-slate-600 text-white mt-1"
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={inviteUser}
                    disabled={inviting}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="invite-user-submit"
                  >
                    {inviting ? 'Inviting...' : 'Send Invitation'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Season Management Modal */}
        <Dialog open={showSeasonModal} onOpenChange={setShowSeasonModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Season Management - {selectedProgram?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* New Season Form */}
              {showNewSeasonForm ? (
                <Card className="p-4 bg-slate-900/50 border-slate-600 rounded-xl">
                  <h4 className="font-medium text-white mb-4 flex items-center gap-2">
                    <PlayCircle className="w-4 h-4 text-green-400" />
                    Start New Season
                  </h4>
                  
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-400">
                      ⚠️ Starting a new season will reset all youth data (points, onboarding, goals) while preserving historical records.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-300">Season Name *</Label>
                      <Input
                        value={newSeasonForm.season_name}
                        onChange={(e) => setNewSeasonForm({ ...newSeasonForm, season_name: e.target.value })}
                        placeholder="e.g., Summer 2027"
                        className="bg-slate-800 border-slate-600 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Year *</Label>
                      <Input
                        type="number"
                        value={newSeasonForm.year}
                        onChange={(e) => setNewSeasonForm({ ...newSeasonForm, year: parseInt(e.target.value) })}
                        className="bg-slate-800 border-slate-600 text-white mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-300">Start Date</Label>
                        <Input
                          type="date"
                          value={newSeasonForm.start_date}
                          onChange={(e) => setNewSeasonForm({ ...newSeasonForm, start_date: e.target.value })}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">End Date</Label>
                        <Input
                          type="date"
                          value={newSeasonForm.end_date}
                          onChange={(e) => setNewSeasonForm({ ...newSeasonForm, end_date: e.target.value })}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowNewSeasonForm(false)}
                      className="flex-1 border-slate-600 text-slate-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={startNewSeason}
                      disabled={creatingNewSeason}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {creatingNewSeason ? 'Starting...' : 'Start Season'}
                    </Button>
                  </div>
                </Card>
              ) : (
                <Button
                  onClick={() => setShowNewSeasonForm(true)}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="start-new-season-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Start New Season
                </Button>
              )}

              {/* Seasons List */}
              <div>
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Season History
                </h4>
                
                {loadingSeasons ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                ) : seasons.length === 0 ? (
                  <Card className="p-4 bg-slate-900/50 border-slate-600 rounded-xl text-center">
                    <p className="text-slate-400">No seasons yet</p>
                  </Card>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {seasons.slice().reverse().map((season, index) => (
                      <Card 
                        key={season.id} 
                        className={`p-3 rounded-xl ${
                          season.is_active 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-slate-900/50 border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{season.season_name}</span>
                              {season.is_active && (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  Active
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              {season.participant_count || 0} participants
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-400">{season.year}</p>
                            {!season.is_active && (
                              <Badge className="bg-slate-500/20 text-slate-400 text-xs mt-1">
                                <Archive className="w-3 h-3 mr-1" />
                                Archived
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSeasonModal(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Onboard Organization Modal */}
        <Dialog open={showOnboardModal} onOpenChange={setShowOnboardModal}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-500" />
                {onboardResult ? 'Organization Onboarded!' : 'Onboard New Organization'}
              </DialogTitle>
            </DialogHeader>

            {onboardResult ? (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-center font-bold text-emerald-800">{onboardResult.message}</p>
                </div>
                <div className="space-y-2 p-4 rounded-xl bg-gray-50">
                  <p className="text-sm"><strong>Organization:</strong> {onboardResult.organization?.name}</p>
                  <p className="text-sm"><strong>Program:</strong> {onboardResult.program?.name} ({onboardResult.program?.type})</p>
                  <p className="text-sm"><strong>Admin:</strong> {onboardResult.admin?.name} ({onboardResult.admin?.email})</p>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mt-3">
                    <p className="text-xs font-bold text-amber-800 mb-1">Temporary Password (share securely):</p>
                    <p className="text-lg font-mono font-bold text-amber-900">{onboardResult.admin?.temp_password}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">A welcome email has been sent to the admin. They will be prompted to change their password on first login.</p>
                </div>
                <Button onClick={() => { setShowOnboardModal(false); setOnboardResult(null); }} className="w-full">Done</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-bold text-gray-700">Organization Name *</Label>
                  <Input value={onboardForm.org_name} onChange={e => setOnboardForm({...onboardForm, org_name: e.target.value})} placeholder="Camden School District" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-gray-700">Program Name *</Label>
                  <Input value={onboardForm.program_name} onChange={e => setOnboardForm({...onboardForm, program_name: e.target.value})} placeholder="Camden Career Pathways" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-gray-700">Program Type *</Label>
                  <Select value={onboardForm.program_type} onValueChange={v => setOnboardForm({...onboardForm, program_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="syep">SYEP (Summer Youth Employment)</SelectItem>
                      <SelectItem value="student_pathways_gps">Student Pathways GPS (School Districts)</SelectItem>
                      <SelectItem value="youth_development">Youth Development</SelectItem>
                      <SelectItem value="youth_leadership">Youth Leadership</SelectItem>
                      <SelectItem value="workforce_development">Workforce Development</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold text-gray-700">City *</Label>
                    <Input value={onboardForm.city} onChange={e => setOnboardForm({...onboardForm, city: e.target.value})} placeholder="Camden" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold text-gray-700">State *</Label>
                    <Input value={onboardForm.state} onChange={e => setOnboardForm({...onboardForm, state: e.target.value})} placeholder="NJ" className="mt-1" />
                  </div>
                </div>
                <div className="border-t pt-4">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-3">Program Administrator</p>
                  <div>
                    <Label className="text-xs font-bold text-gray-700">Admin Name *</Label>
                    <Input value={onboardForm.admin_name} onChange={e => setOnboardForm({...onboardForm, admin_name: e.target.value})} placeholder="Dr. Maria Santos" className="mt-1" />
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs font-bold text-gray-700">Admin Email *</Label>
                    <Input type="email" value={onboardForm.admin_email} onChange={e => setOnboardForm({...onboardForm, admin_email: e.target.value})} placeholder="m.santos@yasconnect.co" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold text-gray-700">Visual Theme</Label>
                  <Select value={onboardForm.theme} onValueChange={v => setOnboardForm({...onboardForm, theme: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cashapp_clean">Cash App Clean</SelectItem>
                      <SelectItem value="duo_energy">Duo Energy</SelectItem>
                      <SelectItem value="spotify_nights">Spotify Nights</SelectItem>
                      <SelectItem value="tiktok_feed">TikTok Feed</SelectItem>
                      <SelectItem value="smooth_vibes">Smooth Vibes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleOnboard} disabled={onboarding} className="w-full bg-[#C4542D] hover:bg-[#8B3520] text-white font-bold">
                  {onboarding ? 'Onboarding...' : 'Onboard Organization'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View As / Impersonate Modal */}
        <Dialog open={showViewAs} onOpenChange={setShowViewAs}>
          <DialogContent className="bg-white rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-orange-500" /> View As Client Admin</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 mb-4">Select a client organization admin to view their dashboard as if you were them. You'll be able to see everything they see.</p>
            {orgAdmins.length > 0 ? (
              <div className="space-y-2">
                {orgAdmins.map(admin => (
                  <div key={admin.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{admin.name}</p>
                      <p className="text-xs text-gray-500">{admin.organization_name || 'Unknown Org'} — {admin.program_name || 'Unknown Program'}</p>
                      <p className="text-xs text-gray-400">{admin.email} · {admin.role}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => viewAsUser(admin.id)}
                      disabled={viewAsLoading}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs"
                    >
                      <Eye className="w-3 h-3 mr-1" /> View As
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-4">No client admins found</p>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;
